import React, { useEffect, useState } from 'react';
import { Card, Tag, Alert, Space, Typography } from 'antd';
import api from '../../services/api';

const { Text } = Typography;

/**
 * Suggested priority per line, and the groups that save the customer money.
 *
 * Two things the owner asked for, shown together because they answer the same
 * question: "I cannot do all of this today — what now, and what can wait?"
 *
 * Everything here is a SUGGESTION. The priorities are computed from the job
 * descriptions and are explicitly labelled as such, so nobody mistakes them for a
 * decision the shop has made. A group is an offer, never a requirement — the
 * customer may only be able to afford one item in it.
 *
 * The saving is stated AS a saving, in hours and money. Quietly re-pricing a
 * combined job would read as though the first quote had been padded.
 */

interface PriorityRow {
  id: number;
  priority: 1 | 2 | 3 | 4;
  source: 'set' | 'suggested';
  label: string;
}

interface GroupRow {
  key: string;
  label: string;
  lineIds: number[];
  savedHours: number;
  savedAmount: number;
  summary: string;
}

const PRIORITY_COLOUR: Record<number, string> = {
  1: 'red',
  2: 'orange',
  3: 'blue',
  4: 'default',
};

export interface RepairPriorityCardProps {
  estimateId: number | string;
  /** Line descriptions, so a priority can be shown against the right job. */
  lines: Array<{ id: number; description: string }>;
}

const RepairPriorityCard: React.FC<RepairPriorityCardProps> = ({ estimateId, lines }) => {
  const [priorities, setPriorities] = useState<PriorityRow[]>([]);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Awaited inside the effect rather than chained, so the rejection is consumed
    // where it is raised. A floating `.then().catch()` chain leaves a promise the
    // effect no longer owns, and on a fetch that rejects after the component is
    // gone the runner sees it as an unhandled rejection with no test to blame.
    (async () => {
      try {
        const res = await api.get(`/estimates/${estimateId}/grouping`);
        if (cancelled) return;
        setPriorities(res.data?.priorities ?? []);
        setGroups(res.data?.groups ?? []);
      } catch {
        // A failure here must not take the estimate down with it — the quote is
        // still correct without the suggestion layered on top.
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [estimateId]);

  if (failed || (!priorities.length && !groups.length)) return null;

  const describe = (id: number) => lines.find((l) => l.id === id)?.description ?? `Line ${id}`;
  const byPriority = [...priorities].sort((a, b) => a.priority - b.priority);

  return (
    <Card
      title="Priority & savings"
      extra={<Tag>Suggested — set your own on the line</Tag>}
      style={{ marginBottom: 24 }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {groups.map((g) => (
          <Alert
            key={g.key}
            type="success"
            showIcon
            message={g.summary}
            description={
              <div style={{ fontSize: 12 }}>
                {g.lineIds.map((id) => (
                  <div key={id}>· {describe(id)}</div>
                ))}
                <Text type="secondary">
                  An offer, not a requirement — the customer can still do one of these now.
                </Text>
              </div>
            }
          />
        ))}

        <div>
          {byPriority.map((p) => (
            <div
              key={p.id}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'baseline',
                padding: '4px 0',
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <Tag color={PRIORITY_COLOUR[p.priority]}>P{p.priority}</Tag>
              <span style={{ fontSize: 13, flex: 1 }}>{describe(p.id)}</span>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {p.label}
                {p.source === 'suggested' ? '' : ' (set)'}
              </Text>
            </div>
          ))}
        </div>
      </Space>
    </Card>
  );
};

export default RepairPriorityCard;
